import {
	PersonButtonsContainer,
	PersonCommunicationSettingsContent,
	PricingFormatLabel
} from "components/Preferences/PeopleSettings/PersonCommunicationSettings.styles.ts";
import { Button, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import {
	NewUserPermissionsSaveErrorText,
	PersonSettingsHeaderText,
	PriceFormatList,
	PriceFormatNet,
	PriceFormatNetWithMultiplier,
	PricingFormatSettingsLabel,
	SavingUserPermissionsText,
	UserPermissionsSuccessfullySavedText
} from "components/Preferences/text.ts";
import { RadioGroupContainer } from "components/Preferences/UserSettings/CommunicationSettings.styles.ts";
import { SetStateAction, useState } from "react";
import { models } from "types/api/viewModels.ts";
import AcknowledgementLinePreview from "components/Preferences/AcknowledgementLinePreview.tsx";
import { useUpdateUserPermissionsMutation } from "features/api/permissionsApi.ts";
import { useNavigate } from "react-router";
import { UpdatePersonPermissionsRequest } from "types/api/permissions/updatePerson.ts";
import Snackbar from "components/Common/SnackBar/SnackBar.tsx";
import useSnackbarEffect from "hooks/useSnackbarEffect.ts";
import { CancelButtonText, SaveButtonText } from "constants/text.ts";
import { AcknowledgementPriceFormat, AcknowledgmentEmailFormat } from "types/declarations/enums.ts";

interface PersonCommunicationSettingsProps {
	person?: models["PersonPermissionsViewModel"];
}

const PersonCommunicationSettings = ({ person }: PersonCommunicationSettingsProps) => {
	const [radioValue, setRadioValue] = useState<models["AcknowledgementPriceFormat"]>(
		person?.acknowledgementPriceFormat ?? AcknowledgementPriceFormat.LIST
	);
	const navigate = useNavigate();
	const [updatePerson] = useUpdateUserPermissionsMutation();
	const [isSaving, setIsSaving] = useState(false);
	const [savePermissionsErrorMessage, setSavePermissionsErrorMessage] = useState<string | undefined>(undefined);
	const { snackbarOpen } = useSnackbarEffect(isSaving, savePermissionsErrorMessage);

	const handleOnRadioChanged = (event: SetStateAction<any>, value: any) => {
		setRadioValue(value);
	};

	const cancelChanges = () => {
		navigate("/admin-settings/people");
	};

	const saveChanges = async () => {
		setIsSaving(true);
		try {
			setSavePermissionsErrorMessage(undefined);
			const requestBody: UpdatePersonPermissionsRequest = {
				...person,
				acknowledgementPriceFormat: radioValue
			};
			await updatePerson(requestBody).unwrap();
			setIsSaving(false);
			setTimeout(() => {
				navigate("/admin-settings/people");
			}, 1000);
		} catch (e) {
			console.error("Save permissions failed: ", e);
			setSavePermissionsErrorMessage(NewUserPermissionsSaveErrorText);
			setIsSaving(false);
		}
	};

	return (
		<PersonCommunicationSettingsContent>
			<div>{PersonSettingsHeaderText}</div>
			<PricingFormatLabel>{PricingFormatSettingsLabel}</PricingFormatLabel>
			<RadioGroupContainer>
				<RadioGroup
					value={radioValue}
					onChange={handleOnRadioChanged}
				>
					<FormControlLabel
						control={<Radio />}
						value={AcknowledgementPriceFormat.LIST}
						label={PriceFormatList}
					/>
					<FormControlLabel
						control={<Radio />}
						value={AcknowledgementPriceFormat.NET}
						label={PriceFormatNet}
					/>
					<FormControlLabel
						control={<Radio />}
						value={AcknowledgementPriceFormat.NET_MULTIPLIER}
						label={PriceFormatNetWithMultiplier}
					/>
				</RadioGroup>
			</RadioGroupContainer>
			<AcknowledgementLinePreview
				priceType={radioValue}
				layout={AcknowledgmentEmailFormat.EXPANDED}
			/>
			<AcknowledgementLinePreview
				priceType={radioValue}
				layout={AcknowledgmentEmailFormat.CONDENSED}
			/>
			<PersonButtonsContainer>
				<Button
					variant="outlined"
					onClick={cancelChanges}
				>
					{CancelButtonText}
				</Button>
				<Button
					type="submit"
					variant="contained"
					onClick={saveChanges}
				>
					{SaveButtonText}
				</Button>
			</PersonButtonsContainer>
			<Snackbar
				open={snackbarOpen}
				isLoading={isSaving}
				loadingText={SavingUserPermissionsText}
				successText={UserPermissionsSuccessfullySavedText}
				isError={savePermissionsErrorMessage !== undefined}
				errorText={savePermissionsErrorMessage ?? ""}
			/>
		</PersonCommunicationSettingsContent>
	);
};

export default PersonCommunicationSettings;
